import { useCallback, useState } from 'react';
import type { EnvSchema } from '../EnvSelector';
import { callEdgeFunction } from '../supabase.client';
import type { TableName } from '../TableSidebar';
import type { ChildRel, ParentFK } from './relationships';

type RowMap = Record<string, unknown>;

/**
 * Shared FK expand state for `<NestedTable>` and `<TableEditor>`.
 *
 * Owns four pieces of state:
 *  - `expanded`: row keys that should render a sub-row (children panel +
 *    parent expansions).
 *  - `parentExpanded`: keys of the form `${rowKey}::${fkColumn}` that should
 *    render the parent record above the children panel.
 *  - `childCache` / `parentCache`: lazy-loaded EF responses, keyed identically.
 *  - `loadingChildren` / `parentLoading`: in-flight set for spinner UI.
 *
 * Callers wire `loadChildren` / `loadParent` to UI affordances; the hook itself
 * makes no UI assumptions.
 */
export function useFkExpand(env: EnvSchema) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [childCache, setChildCache] = useState<Record<string, RowMap[]>>({});
  const [loadingChildren, setLoadingChildren] = useState<Set<string>>(new Set());
  const [parentCache, setParentCache] = useState<Record<string, RowMap[]>>({});
  const [parentLoading, setParentLoading] = useState<Set<string>>(new Set());
  const [parentExpanded, setParentExpanded] = useState<Set<string>>(new Set());

  const loadChildren = useCallback(
    async (rowK: string, parentRow: RowMap, child: ChildRel) => {
      const k = `${rowK}::${child.table}`;
      if (childCache[k]) return;
      setLoadingChildren((s) => new Set(s).add(k));
      try {
        const filter = child.via.map((v) => ({
          column: v.childCol,
          op: 'eq',
          value: parentRow[v.localCol],
        }));
        const r = await callEdgeFunction<{ rows: RowMap[] }>('crud', {
          schema: env,
          table: child.table,
          op: 'list',
          filter,
          limit: 1000,
        });
        setChildCache((c) => ({ ...c, [k]: r.rows }));
      } finally {
        setLoadingChildren((s) => {
          const n = new Set(s);
          n.delete(k);
          return n;
        });
      }
    },
    [env, childCache]
  );

  const refreshChildren = useCallback(
    async (rowK: string, parentRow: RowMap, child: ChildRel) => {
      const k = `${rowK}::${child.table}`;
      setChildCache((c) => {
        const next = { ...c };
        delete next[k];
        return next;
      });
      // After dropping the cache, re-run the loader.
      await (async () => {
        setLoadingChildren((s) => new Set(s).add(k));
        try {
          const filter = child.via.map((v) => ({
            column: v.childCol,
            op: 'eq',
            value: parentRow[v.localCol],
          }));
          const r = await callEdgeFunction<{ rows: RowMap[] }>('crud', {
            schema: env,
            table: child.table,
            op: 'list',
            filter,
            limit: 1000,
          });
          setChildCache((c) => ({ ...c, [k]: r.rows }));
        } finally {
          setLoadingChildren((s) => {
            const n = new Set(s);
            n.delete(k);
            return n;
          });
        }
      })();
    },
    [env]
  );

  const loadParent = useCallback(
    async (rowK: string, p: ParentFK, value: unknown) => {
      const cacheKey = `${rowK}::${p.column}`;
      if (parentCache[cacheKey]) return;
      setParentLoading((s) => new Set(s).add(cacheKey));
      try {
        if (p.isArray) {
          const ids = Array.isArray(value) ? value : [];
          if (ids.length === 0) {
            setParentCache((c) => ({ ...c, [cacheKey]: [] }));
            return;
          }
          const r = await callEdgeFunction<{ rows: RowMap[] }>('crud', {
            schema: env,
            table: p.refTable,
            op: 'list',
            filter: [{ column: p.refColumn, op: 'in', value: `(${ids.join(',')})` }],
            limit: 1000,
          });
          setParentCache((c) => ({ ...c, [cacheKey]: r.rows }));
        } else {
          const r = await callEdgeFunction<{ row: RowMap | null }>('crud', {
            schema: env,
            table: p.refTable,
            op: 'get',
            pk: { [p.refColumn]: value },
          });
          setParentCache((c) => ({ ...c, [cacheKey]: r.row ? [r.row] : [] }));
        }
      } finally {
        setParentLoading((s) => {
          const n = new Set(s);
          n.delete(cacheKey);
          return n;
        });
      }
    },
    [env, parentCache]
  );

  const toggleRowExpanded = useCallback((rowK: string) => {
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(rowK)) n.delete(rowK);
      else n.add(rowK);
      return n;
    });
  }, []);

  const toggleParentExpanded = useCallback((cacheKey: string) => {
    setParentExpanded((s) => {
      const n = new Set(s);
      if (n.has(cacheKey)) n.delete(cacheKey);
      else n.add(cacheKey);
      return n;
    });
  }, []);

  const collapseRow = useCallback((rowK: string) => {
    setExpanded((s) => {
      if (!s.has(rowK)) return s;
      const n = new Set(s);
      n.delete(rowK);
      return n;
    });
  }, []);

  const ensureRowExpanded = useCallback((rowK: string) => {
    setExpanded((s) => {
      if (s.has(rowK)) return s;
      const n = new Set(s);
      n.add(rowK);
      return n;
    });
  }, []);

  return {
    expanded,
    parentExpanded,
    childCache,
    loadingChildren,
    parentCache,
    parentLoading,
    loadChildren,
    refreshChildren,
    loadParent,
    toggleRowExpanded,
    toggleParentExpanded,
    collapseRow,
    ensureRowExpanded,
  };
}

export type FkExpandState = ReturnType<typeof useFkExpand>;

export function buildParentCacheKey(rowK: string, fkColumn: string): string {
  return `${rowK}::${fkColumn}`;
}

export function buildChildCacheKey(rowK: string, childTable: TableName): string {
  return `${rowK}::${childTable}`;
}
